import React from 'react';
import { useTranslation } from 'react-i18next';
import { KoobButton, KoobTooltip } from '@koob/ui';
import { getFolderStatus, isFolderArchivable } from '@koob/utils';
import type { FolderActionFolder } from './types/folderActions';

export interface FolderArchiveButtonProps {
  folder: FolderActionFolder;
  onArchive: () => void;
  onUnarchive: () => void;
}

/**
 * KD-3842: Archive (New / Draft folders only, the tooltip names the current
 * status otherwise) or Unarchive once archived.
 */
export default function FolderArchiveButton({
  folder,
  onArchive,
  onUnarchive,
}: FolderArchiveButtonProps) {
  const { t } = useTranslation('folders');
  const archived = Boolean(folder.archivedAt);
  const archivable = isFolderArchivable(folder);

  let tip = t('actions.archiveHint');
  if (archived) {
    tip = t('actions.unarchiveHint');
  } else if (!archivable) {
    tip = t('actions.archiveDisabled', {
      status: t(`folderStatus.${getFolderStatus(folder)}`),
    });
  }

  return (
    <KoobTooltip tip={tip}>
      <span className="inline-flex">
        <KoobButton
          variant="secondary"
          onClick={archived ? onUnarchive : onArchive}
          disabled={!archived && !archivable}
          data-testid={archived ? 'folder-unarchive' : 'folder-archive'}
        >
          <i
            className={`fa-regular ${archived ? 'fa-box-open' : 'fa-box-archive'}`}
            aria-hidden="true"
          />
          {archived ? t('actions.unarchive') : t('actions.archive')}
        </KoobButton>
      </span>
    </KoobTooltip>
  );
}
